import PhotoSwipeLightbox from "photoswipe/lightbox"
import PhotoSwipe from "../../../../../node_modules/photoswipe/dist/photoswipe.esm"
import { inject } from "vue"

export type PhotoSwipeItem = {
  src: string
  width: number
  height: number
  authorName?: string
}

export type PhotoSwipeFetch = (index: number) => Promise<PhotoSwipeItem>

const buildDynamicLoader =
  (lightbox: PhotoSwipeLightbox, fetchFunction: PhotoSwipeFetch) =>
  async ({ content }) => {
    const { pswp } = lightbox
    if (!pswp) {
      return
    }
    const index = content.index
    if (pswp.options.dataSource[index]) {
      return
    }
    const loadedItem = await fetchFunction(index)
    pswp.options.dataSource[index] = loadedItem
    pswp.refreshSlideContent(index)
  }

export class LightboxService {
  callback: (image: string) => void

  openLightbox(image: string) {
    this._validateRegistration()
    if (!image) {
      throw new Error("No valid image URL was passed to the LightboxService.")
    }
    this.callback(image)
  }

  async openPhotoLightbox(
    startIndex: number,
    totalItems: number,
    fetchFunction: PhotoSwipeFetch
  ) {
    const lightbox = new PhotoSwipeLightbox({
      pswpModule: PhotoSwipe,
      index: startIndex,
      dataSource: [],
      loop: false,
    })
    lightbox.init()
    this._registerCaption(lightbox)
    this._registerDownloadButton(lightbox)
    this._setTotalItems(lightbox, totalItems)
    this._setDynamicLoader(lightbox, fetchFunction)
    lightbox.loadAndOpen(startIndex)
  }

  private _registerCaption(lightbox: PhotoSwipeLightbox) {
    lightbox.on("uiRegister", function () {
      lightbox.pswp.ui.registerElement({
        name: "custom-caption",
        order: 9,
        isButton: false,
        appendTo: "root",
        html: "Caption text",
        onInit: (el, pswp) => {
          lightbox.pswp.on("change", () => {
            console.log(lightbox.pswp.currSlide.data)
            const currSlide = lightbox.pswp.currSlide.data
            const captionHTML = currSlide.authorName
            const takenBy = "Aufgenommen von "
            if (captionHTML) {
              el.style.display = "block"
              el.innerHTML = takenBy + captionHTML
            } else {
              el.style.display = "none"
            }
          })
        },
      })
    })
  }

  private _registerDownloadButton(lightbox: PhotoSwipeLightbox) {
    lightbox.on("uiRegister", function () {
      lightbox.pswp.ui.registerElement({
        name: "download-button",
        order: 8,
        isButton: true,
        tagName: "a",

        // SVG with outline
        html: {
          isCustomSVG: true,
          inner:
            '<path d="M20.5 14.3 17.1 18V10h-2.2v7.9l-3.4-3.6L10 16l6 6.1 6-6.1ZM23 23H9v2h14Z" id="pswp__icn-download"/>',
          outlineID: "pswp__icn-download",
        },

        onInit: (el, pswp) => {
          el.setAttribute("download", "")
          el.setAttribute("target", "_blank")
          el.setAttribute("rel", "noopener")

          pswp.on("change", () => {
            ;(el as HTMLAnchorElement).href = pswp.currSlide.data.src
          })
        },
      })
    })
  }

  private _setTotalItems(lightbox: PhotoSwipeLightbox, totalItems: number) {
    lightbox.addFilter("numItems", (items) => {
      return totalItems
    })
  }

  private _setDynamicLoader(
    lightbox: PhotoSwipeLightbox,
    fetchFunction: PhotoSwipeFetch
  ) {
    lightbox.on("contentInit", buildDynamicLoader(lightbox, fetchFunction))
    lightbox.on("contentActivate", buildDynamicLoader(lightbox, fetchFunction))
  }

  private _validateRegistration() {
    if (!this.callback) {
      throw new Error(
        "No Lightbox was registered in the LightboxService. Maybe you forgot to pass the service to the Lightbox component?"
      )
    }
  }

  registerCallback(callback: (image: string) => void) {
    this.callback = callback
  }
}

export function useLightbox() {
  const lightboxService = inject("lightboxService", null) as LightboxService
  if (!lightboxService) {
    console.warn("No LightboxService was found. Did you forget to provide it?")
  }
  return lightboxService
}
